import { getProduct } from './pre-tailoring.requests';

import { useQuery } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';

import { BuildingBlocks } from '@/components/elements/building-blocks/building-blocks.component';
import { BuildingBlockDetails } from '@/components/elements/building-blocks/details-building-block/details-building-block.component';
import { TailoringHeading } from '@/components/elements/tailoring-heading/tailoring-heading.component';
import { T } from '@/components/i18n/message.i18n.component';
import { PreTailoringLayout } from '@/components/layout/pre-tailoring/pre-tailoring.layout';
import { Answer, LifecycleStatus, Product, ProductBuildingBlock, YesNoQuestion } from '@/gql/graphql';
import { useBuildingBlocks } from '@/hooks/use-building-blocks/use-building-blocks.hook';
import { useQuestions } from '@/hooks/use-questions/use-questions.hook';
import { useDialogStore } from '@/store/dialog.store';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { pretailoringPageMessages } from './pre-tailoring.messages';
import { QuestionsHeading } from './questions/questions-heading.component';
import { Questions } from './questions/questions.component';

export const PreTailoringPage = () => {
  const t = useIntl();
  const { id } = useParams();

  const navigate = useNavigate();

  const { openDialog, closeDialog } = useDialogStore((state) => state);
  const { data, loading } = useQuery<{ product: Product }>(getProduct, {
    variables: { id },
  });

  const {
    progress,
    search,
    filter,
    radioQuestions,
    allAnswered,
    updateQuestion,
    handleQuestionFilter,
    answerInProgress,
  } = useQuestions({
    product: data?.product,
  });

  const {
    includedBuildingBlocks,
    excludedBuildingBlocks,
    sort,
    handleSortChange: onSortChange,
  } = useBuildingBlocks({
    buildingBlocks: data?.product.tailoringProcess.buildingBlocks,
  });

  const onQuestionAnswer = async (question: YesNoQuestion, value: string) => {
    const answer = value.toUpperCase() as Answer;
    updateQuestion(question, answer, data?.product);
  };

  const areQuestionsDisabled = useMemo(() => {
    if (!data?.product) return true;
    if (data?.product?.lifecycleStatus === LifecycleStatus.Retired) return true;
    if (loading) return true;
    return false;
  }, [data?.product?.lifecycleStatus, loading]);

  const handleMoreInfo = (bb: ProductBuildingBlock) => {
    openDialog({
      type: 'custom',
      fullScreen: true,
      title: t.formatMessage(pretailoringPageMessages.singleBuildingBlockModalTitle, {
        name: bb.name,
      }),
      element: (
        <div className="relative inline-flex flex-1 overflow-scroll">
          <div className="absolute top-0 left-0 right-0 flex flex-col gap-3">
            <BuildingBlockDetails buildingBlock={bb} />
          </div>
        </div>
      ),
    });
  };

  const onNext = () => {
    if (areQuestionsDisabled) return navigate(`/product/${data?.product?.id}/tailoring`);
    if (allAnswered) return navigate(`/product/${data?.product?.id}/tailoring`);
    openDialog({
      type: 'confirmation',
      title: <T {...pretailoringPageMessages.notAllQuestionsAnsweredModalTitle} />,
      confirmationText: <T {...pretailoringPageMessages.notAllQuestionsAnsweredModalText} />,
      onConfirm: () => {
        closeDialog();
        navigate(`/product/${data?.product?.id}/tailoring`);
      },
    });
  };

  const handleToProducts = () => {
    navigate('/products');
  };

  return (
    <PreTailoringLayout
      onToProducts={handleToProducts}
      loading={loading}
      progress={progress}
      title={<TailoringHeading context="pre-tailoring" product={data?.product} />}
      questionsHeading={
        <QuestionsHeading search={search} filter={filter} onFilterSearchChange={handleQuestionFilter} />
      }
      left={
        data?.product && (
          <Questions
            disabled={areQuestionsDisabled}
            handleSubmit={onNext}
            radioQuestions={radioQuestions}
            handleQuestionAnswer={onQuestionAnswer}
          />
        )
      }
      right={
        data?.product && (
          <BuildingBlocks
            sort={sort}
            loading={answerInProgress}
            includedBuildingBlocks={includedBuildingBlocks}
            excludedBuildingBlocks={excludedBuildingBlocks}
            isProductActive={data?.product.lifecycleStatus === LifecycleStatus.Active}
            handleSortChange={onSortChange}
            onBlockClick={handleMoreInfo}
          />
        )
      }
    />
  );
};
